import {Component,Inject,OnDestroy,OnInit} from '@angular/core';
import {MatLegacySelectChange as MatSelectChange} from "@angular/material/legacy-select";
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,MatLegacyDialogRef as MatDialogRef} from '@angular/material/legacy-dialog'
import {TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';
import {Store} from '@ngrx/store';
import {Observable,of,Subscription} from 'rxjs';

import {Anomalie,SettingsODState} from '@domain/settings/settings';
import {TypePrestation} from '@domain/prestation/prestation';
import {ConfirmService} from '@share/component/confirmation/confirm.service';
import {AppState} from '@domain/appstate';
import {SaisieState} from '@domain/saisie/saisie';
import {FraisPrev} from "@domain/fraisprev/frais-prev";
import {Od} from "@domain/od/od";
import {ODService} from "../../../../../od.service";
import {TypeGeographie} from "@domain/geographie/typeGeographie";
import {filter,finalize,first} from "rxjs/operators";
import {PrestTaux} from "@domain/prestation/prest-taux";
import {FraisService} from "@components/frais/frais.service";
import {TypePortee} from "@domain/workflow/workflow";
import * as moment from "moment";
import {FraisMission} from "@domain/od/engagements/indemnites/frais-mission";
import {TypeBareme} from "@domain/prestation/TypeBareme";

/**
 * Composant d'affichage du formulaire d'ajout d'un frais prévisionnel
 */
@Component({
    templateUrl: './od-frais-previsionnel-add.component.html'
})
export class OdFraisPrevisionnelAddComponent implements OnInit,OnDestroy {
    /* Déclaration pour accès dans le template */
    TypePrestation = TypePrestation;
    TypePortee = TypePortee;
    TypeBareme = TypeBareme;

    /** Indicateur d'enregistrement en cours */
    isSaving: boolean = false;

    /** Indicateur de suppression en cours */
    isDeleting: boolean = false;
    
    /** Indicateur du chargement en cours */
    isLoading: boolean = false;

    /** Indicateur de traitement en cours (enregistrement ou suppression) */
    get isProcessing(): boolean {
        return this.isSaving ||this.isDeleting;
    }

    /** Informations de saisie */
    saisieState: SaisieState = null;

    /** Date du frais */
    maDate: moment.Moment; 

    /** Liste des souscriptions */
    private listeSouscriptions: Array<Subscription> = new Array<Subscription>();

    /** Permet de sauvegarder le taux pour le réintégrer si besoin */
    private tauxCache: FraisMission;

    /** Montant calculé dans le cas d'une prestation de type barème SNCF */
    protected montantFraisPrevBaremeSNCF: number;

    /**
     * Constructeur
     */
    constructor(private odService: ODService,
                private store: Store<AppState>,
                private toastrService: ToastrService,
                private translateService: TranslateService,
                private confirmService: ConfirmService,
                public fraisService: FraisService,
                private matDialogRef: MatDialogRef<any,any>,
                @Inject(MAT_DIALOG_DATA) public data: { fraisPrev: FraisPrev,od: Od,settings: SettingsODState,canModifier: boolean }) {

    }

    /**
     * Initialisation
     */
    ngOnInit() {
        //Chargement d'un frais prévisionnel existant
        if (this.data.fraisPrev?.idFraisPrev > 0) {
            //Chargement des données
            this.isLoading = true;

            //Chargement du frais prévisionnel
            this.odService.loadFraisPrev(this.data.fraisPrev?.idFraisPrev)
                .pipe(finalize(() => this.isLoading = false))
                .subscribe((fraisPrev: FraisPrev) => {
                    this.data.fraisPrev = fraisPrev;

                    //Initialisation
                    this.init();
                });
        } else {
            //Initialisation
            this.init();
        }
    }

    /**
     * Permet de récupérer le montant après calcul dans le cas d'une prestation de type barème SNCF
     */
    getMontantFraisPrevBaremeSNCF(): Observable<number> {
        //Vérification de la présence des données nécéssaires au calcul du montant
        if (this.data.fraisPrev?.prestation?.typeBareme == TypeBareme.BAREME_SNCF
            && this.maDate?.isValid()
            && this.data.fraisPrev.prestation?.idPrestation > 0
            && !!this.data.fraisPrev.quantite
            && this.data.fraisPrev.devise != null
        ) {
            //Renvoi de l'observable permettant de calculer le montant à la souscription
            return this.odService.computeMontantFraisPrevBaremeSNCF(
                this.data.fraisPrev.prestation.idPrestation,
                this.maDate.toDate(),
                this.data.fraisPrev.quantite,
                this.data.fraisPrev.devise
            );
        }

        return of (0);
    }

    /**
     * Lance le calcul côté back du montant en fonction du barème SNCF de la prestation
     */
    computeMontantFraisPrevBaremeSNCF(): void {
        this.getMontantFraisPrevBaremeSNCF().pipe(first()).subscribe(montant => {
           this.montantFraisPrevBaremeSNCF = montant;
        });
    }

    /**
     * Retourne le montant calculé d'une prestation de type forfait.
     * Si la prestation n'est pas de ce type, retourne undefined.
     */
    getMontantForfait(): number {
        //Vérification du type de prestation Forfait
        if (this.data.fraisPrev.prestation?.type == TypePrestation.FORFAIT) {
            //Calcul
            return ((this.fraisService.getValueAsNumber(this.data.fraisPrev.quantite) || 1)
                * (this.getTauxForPrestation() || 0));
        } else {
            return undefined;
        }
    }

    /**
     * Retourne le montant (après calcul le cas échéant)
     */
    getMontant(): Observable<number> {
        if (this.data.fraisPrev.prestation.typeBareme == TypeBareme.BAREME_SNCF) {
            //Cas d'une prestation de type barème SNCF => calcul back
            return this.getMontantFraisPrevBaremeSNCF();
        } else if (this.data.fraisPrev.prestation?.type == TypePrestation.FORFAIT) {
            //Dans le cas d'une prestation de type forfait le montant n'est pas saisissable mais calculé
            return of(this.getMontantForfait() ?? 0);
        } else {
            //Retour du montant saisi
            return of(this.data.fraisPrev.montant ?? 0);
        }
    }

    /**
     * Initialisation post-chargement de l'objet
     */
    init(): void {
        //Définition de la géographie
        this.data.fraisPrev.geographie = null;

        //Vérification du type de lieu
        if (this.data.fraisPrev.ville) {
            //Construction d'un objet géographie de type Ville
            this.data.fraisPrev.geographie = {
                id: this.data.fraisPrev.ville.idVille,
                type: TypeGeographie.VILLE,
                libelle: this.data.fraisPrev.ville.libelle,
                libelleSain: this.data.fraisPrev.ville.libelleNettoye,
                ville: this.data.fraisPrev.ville,
                regionAdmin: this.data.fraisPrev.ville.regionAdmin,
                departementAdmin: this.data.fraisPrev.ville.departementAdmin,
                pays: this.data.fraisPrev.pays
            }
        } else if (this.data.fraisPrev.pays) {
            //Construction d'un objet géographie de type Pays
            this.data.fraisPrev.geographie = {
                id: this.data.fraisPrev.pays.idPays,
                type: TypeGeographie.PAYS,
                libelle: this.data.fraisPrev.pays.libelle,
                libelleSain: this.data.fraisPrev.pays.libelleNettoye,
                pays: this.data.fraisPrev.pays
            }
        }

        //Ecoute de la saisie
        this.listeSouscriptions.push(
            //Souscription pour observer la nécéssité de mettre à jour la combo de sélection des indemnités
            this.store.select(state => state.saisie).subscribe((saisieState) => {
                this.saisieState = saisieState;
                if (this.saisieState?.tauxPrestation) {
                    if (this.data.fraisPrev.taux) {
                        if (this.findTaux(this.data.fraisPrev.taux.idTaux) == null) {
                            this.data.fraisPrev.taux = null;
                            this.updateTauxWithDefaut();
                        }
                    } else if (this.tauxCache != null && saisieState?.tauxPrestation?.allTaux?.some(taux => taux.idTaux === this.tauxCache.idTaux)) {
                        //Si on a un taux en cache et qu'il est compatible avec la prestation courante
                        //On le réintègre
                        this.data.fraisPrev.taux = this.tauxCache;
                    } else {
                        if (this.data.fraisPrev.idFraisPrev == 0) {
                            //Si on n'a pas de taux et qu'on est en création alors on saisie le taux par défaut
                            this.updateTauxWithDefaut();
                        }
                    }
                }
            })
        );

        //Construction de l'objet moment à partir de la date du frais
        this.maDate = this.data.fraisPrev.date ? moment(this.data.fraisPrev.date) : null;

        //Mise à jour du taux de prestation
        this.refreshTauxPrestation();
    }

    /**
     * Destruction du composant
     */
    ngOnDestroy() {
        //Suppression des souscriptions
        this.listeSouscriptions.forEach(s => s.unsubscribe());
    }

    /**
     * Changement de prestation
     */
    onPrestationChange(prestation: any) {
        //Si on a un taux
        if (this.data.fraisPrev.taux != null) {
            //On le renseigne en cache
            this.tauxCache = this.data.fraisPrev.taux;
        }

        this.data.fraisPrev.taux = null;

        //Mise à jour de la quantité
        this.fraisService.onPrestationChangeUpdateQuantite(prestation,this.data.fraisPrev);

        //Rafraichissement du taux de prestation si nécessaire
        this.refreshTauxPrestation();
    }

    /**
     * Changement de géographie
     */
    onGeographieChange(geographie: any) {
        //Rafraichissement du taux de prestation si nécessaire
        this.refreshTauxPrestation();
    }

    /**
     * Changement de date
     */
    onDateChange() {
        //Mise à jour de la date sur le frais
        this.data.fraisPrev.date = this.maDate?.toDate();

        //Rafraichissement du taux de prestation si nécessaire
        this.refreshTauxPrestation();
    }

    /**
     * Enregistrement du frais prévisionnel
     */
    saveFraisPrev() {
        //Enregistrement en cours
        this.isSaving = true;

        //Mise à jour de la ville / pays sur l'objet à partir de l'autocomplete
        this.data.fraisPrev.pays = this.data.fraisPrev.geographie.pays;
        this.data.fraisPrev.ville = this.data.fraisPrev.geographie.ville || null;

        //Récupération du montant
        this.getMontant().subscribe(montant => {
            //Mise à jour du montant
            this.data.fraisPrev.montant = montant;

            //Enregistrement de la dépense
            this.odService.saveFraisPrev(this.data.fraisPrev)
                .pipe(finalize(() => this.isSaving = false))
                .subscribe({
                    next: idFraisPrev => {
                        //Vérification de l'enregistrement
                        if (idFraisPrev) {
                            //Fermeture de l'écran (on passe true pour déclencher le rechargement de la liste)
                            this.matDialogRef.close({refresh: true});
                        } else {
                            //Message d'erreur
                            this.toastrService.error(this.translateService.instant('global.errors.enregistrement'));
                        }
                    },
                    error: () => {
                        this.toastrService.error(this.translateService.instant('global.errors.enregistrement'));
                    }
                });
        },() => {
            //Message d'erreur
            this.toastrService.error(this.translateService.instant('global.errors.enregistrement'));

            this.isSaving = false;
        });
    }

    /**
     * Suppression du frais prévisionnel
     */
    deleteFraisPrev() {
        //Message de confirmation
        this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation')).pipe(filter(isConfirmed => isConfirmed)).subscribe({
            next: () => {
                //Suppression en cours
                this.isDeleting = true;

                //Suppression
                this.odService.deleteFraisPrev(this.data.fraisPrev)
                    .pipe(finalize(() => this.isDeleting = false))
                    .subscribe({
                        next: (isSuccess) => {
                            //Vérification de la suppression
                            if (isSuccess) {
                                //Fermeture de l'écran (on passe true pour déclencher le rechargement de la liste)
                                this.matDialogRef.close({refresh: true, message: 'global.success.suppression'});
                            } else {
                                //Message d'erreur
                                this.toastrService.error(this.translateService.instant('global.errors.suppression'));
                            }
                        },
                        error: () => {
                            //Message d'erreur
                            this.toastrService.error(this.translateService.instant('global.errors.suppression'));
                        }
                    });
            }
        });
    }

    /**
     * Rafraichissement du taux de prestation
     */
    refreshTauxPrestation() {
        this.fraisService.refreshTauxPrestation(
            this.data.fraisPrev.prestation,
            this.data.fraisPrev.date,
            this.data.fraisPrev.geographie?.pays,
            this.data.fraisPrev.geographie?.ville,
            null
        );

        //Calcul du montant en fonction du barème SNCF le cas échéant
        this.computeMontantFraisPrevBaremeSNCF();
    }

    /**
     * Changement de devise
     *
     * @param change évènement déclenché lors du choix d'une option
     */
    onDeviseChange(change: MatSelectChange) {
        //Mise à jour suite au changement de la devise
        this.fraisService.findCoursForDevise(
            change?.value,
            this.data.fraisPrev.date,
            this.data.settings.deviseEntreprise,
            1
        ).subscribe(cours => {
            this.data.fraisPrev.cours = cours;

            //Calcul du montant en fonction du barème SNCF le cas échéant
            this.computeMontantFraisPrevBaremeSNCF();
        });
    }

    /**
     * Récupération du taux/montant de la prestation
     */
    getTauxForPrestation(): number {
        return this.fraisService.getTauxForPrestation(
            this.saisieState,
            this.data.settings,
            this.data.fraisPrev.devise,
            this.data.fraisPrev.cours,
            this.data.fraisPrev.taux
        );
    }

    /**
     * Retourne le taux correspondant à un id.
     *
     * @param idTaux Identifiant du taux. Si vide/undefined/null, retourne le taux qui sera utilisé "par défaut"
     */
    findTaux(idTaux?: number): PrestTaux {
        return this.fraisService.findTaux(this.saisieState,idTaux);
    }

    /**
     * Renseigne un taux par défaut
     */
    updateTauxWithDefaut(): void {
        const allTaux: PrestTaux[] = this.saisieState?.tauxPrestation?.allTaux;

        //Si on n'a pas de taux sur le frais et qu'on a une liste de taux à disposition
        if (this.data.fraisPrev.taux == null && allTaux != null) {
            //On assigne le taux par défaut qui n'est pas celui avec un libellé spécial ou le premier taux
            this.data.fraisPrev.taux = allTaux.find(value => value.libelleSpecial == null || value.libelleSpecial == '')
                ?? allTaux[0];
        }
    }

    /**
     * Indique si le montant est invalide
     */
    isMontantInvalid(): boolean {
        //Si c'est un plafond et que le montant ne correspond pas au plafond
        return this.data.fraisPrev.prestation?.type === TypePrestation.PLAFOND
            && this.getTauxForPrestation() * this.data.fraisPrev.quantite < this.data.fraisPrev.montant;
    }

    /**
     * Indicateur de contrôle non bloquant sur la date
     *
     * @param date la date à vérifier
     * @returns Un indicateur s'il y a un avertissement sur la date
     */
    isDateHorsMissionWarning(date: Date): boolean {
        //Retourne un indicateur de contrôle non bloquant sur la date
        return this.isDateHorsMissionControle(date, Anomalie.NON_BLOQUANT);
    }

    /**
     * Indicateur de contrôle bloquant sur la date
     *
     * @param date la date à vérifier
     * @returns Un indicateur s'il y a une erreur sur la date
     */
    isDateHorsMissionError(date: Date): boolean {
        //Retourne un indicateur de contrôle bloquant sur la date
        return this.isDateHorsMissionControle(date, Anomalie.BLOQUANT);
    }


    /**
     * Indicateur sur une date hors mission
     *
     * @param date la date à vérifier
     * @param anomalie le type de contrôle à vérifier : BLOQUANT, NON_BLOQUANT
     * @returns Un indicateur si la date est en erreur selon le type de contrôle sur la date hors mission
     */
    isDateHorsMissionControle(date: Date, anomalie: Anomalie): boolean {
        let odDepart: moment.Moment;
        let odRetour: moment.Moment;

        //Si on a une date de saisie et s'il y a un contrôle sur les dates hors mission
        if (!!date
            && this.data?.settings?.anomalieDepensesHorsMission === anomalie
            && this.data?.od?.depart_le
            && this.data?.od?.retour_le) {

            odDepart = moment(this.data.od.depart_le).startOf('day');
            odRetour = moment(this.data.od.retour_le).startOf('day');

            //Si la date est hors mission
            return odDepart.isAfter(date) || odRetour.isBefore(date);
        }

        return false;
    }

    /**
     * Indicateur de contrôle (bloquant ou non bloquant) sur la date hors mission
     *
     * @param date la date à vérifier
     * @returns Un indicateur s'il y a une anomalie (bloquante ou non bloquante) sur la date hors mission
     */
    isDateHorsMissionBloquanteOuNonBloquante(date: Date): boolean {
        //S'il y a un contrôle bloquant ou non bloquant sur la date hors-mission
        return this.isDateHorsMissionControle(date, Anomalie.BLOQUANT)
            || this.isDateHorsMissionControle(date, Anomalie.NON_BLOQUANT);
    }
}